import React from 'react';
import { motion } from 'framer-motion';

const VisionValues = ({ lang }) => {
  const isRtl = lang === 'ar';

  const content = [
    {
      title: isRtl ? 'رؤيتنا' : 'Our Vision',
      desc: isRtl ? 'أن نكون الخيار الأول عالمياً في حلول التنظيف المستدامة، مع الحفاظ على إرث الجودة والابتكار.' : 'To be the global first choice in sustainable cleaning solutions, maintaining our legacy of quality.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      )
    },
    {
      title: isRtl ? 'قيمنا' : 'Our Values',
      desc: isRtl ? 'النزاهة، الالتزام بأعلى معايير الجودة، والمسؤولية تجاه البيئة والمجتمع كركيزة أساسية.' : 'Integrity, commitment to high quality, and environmental responsibility as a core pillar.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      )
    },
    {
      title: isRtl ? 'أهدافنا' : 'Our Goals',
      desc: isRtl ? 'تطوير منتجات صديقة للبيئة، التوسع دولياً، وتحقيق أعلى مستويات رضا العملاء حول العالم.' : 'Developing eco-friendly products, international expansion, and achieving global customer satisfaction.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3" />
        </svg>
      )
    }
  ];

  // إعدادات حركة الحاوية (الأب) لعمل التتابع
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3, // الوقت بين ظهور كل كارت وآخر (0.3 ثانية)
      }
    }
  };

  // إعدادات حركة كل كارت (الابن)
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      scale: 0.9 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.8, 
        ease: [0.16, 1, 0.3, 1] // حركة انسيابية جداً (Out-Expo)
      }
    }
  };

  return (
    <section className="ultra-modern-vision">
      <motion.div 
        className="vision-wrapper"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {content.map((item, index) => (
          <motion.div 
            key={index}
            className="vision-abstract-card"
            variants={cardVariants}
            whileHover={{ y: -10, transition: { duration: 0.3 } }}
          >
            {/* الرأس الفخم */}
            <div className="abstract-header">
              <div className="icon-sphere">
                {item.icon}
                <div className="pulse-ring"></div>
              </div>
              <h3 className="abstract-title">{item.title}</h3>
            </div>

            {/* الجسم الشفاف */}
            <div className="abstract-body">
              <p className="abstract-desc">{item.desc}</p>
            </div>

            <div className="bottom-light-bar"></div>
          </motion.div>
        ))}
      </motion.div>

      <style>{`
        .ultra-modern-vision {
          padding: 150px 20px;
          background: #ffffff;
          direction: ${isRtl ? 'rtl' : 'ltr'};
          overflow: hidden;
        }

        .vision-wrapper {
          display: flex;
          justify-content: center;
          gap: 40px;
          max-width: 1300px;
          margin: 0 auto;
          flex-wrap: wrap;
        }

        .vision-abstract-card {
          flex: 1;
          min-width: 320px;
          position: relative;
          text-align: center;
        }

        .abstract-header {
          position: relative;
          z-index: 3;
          margin-bottom: -20px;
        }

        .icon-sphere {
          width: 75px;
          height: 75px;
          background: #1a3a5a;
          color: #d4af37;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 15px;
          box-shadow: 0 10px 30px rgba(26, 58, 90, 0.2);
          position: relative;
        }

        .icon-sphere svg { width: 35px; height: 35px; stroke: currentColor; }

        .pulse-ring {
          position: absolute;
          width: 100%; height: 100%;
          border: 1px solid #d4af37;
          border-radius: 50%;
          animation: pulse 2.5s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.8); opacity: 0; }
        }

        .abstract-title {
          font-family: 'Cairo', sans-serif !important;
          font-size: 2.8rem;
          font-weight: 900;
          color: #1a3a5a;
          margin: 0;
        }

        .abstract-body {
          background: rgba(255, 255, 255, 0.01);
          border-radius: 45px;
          padding: 60px 35px 45px;
          border: 1px solid rgba(0,0,0,0.04);
          transition: 0.4s ease;
        }

        .abstract-desc {
          font-family: 'Cairo', sans-serif !important;
          font-size: 1.4rem;
          line-height: 1.8;
          color: #475569;
          font-weight: 700;
          margin: 0;
        }

        .vision-abstract-card:hover .abstract-body {
          background: #ffffff;
          box-shadow: 0 40px 80px rgba(0,0,0,0.06);
          border-color: rgba(212, 175, 55, 0.2);
        }

        .bottom-light-bar {
          width: 0; height: 3px;
          background: linear-gradient(90deg, transparent, #d4af37, transparent);
          margin: 25px auto 0;
          transition: 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .vision-abstract-card:hover .bottom-light-bar {
          width: 70%;
        }

        @media (max-width: 768px) {
          .abstract-title { font-size: 2.2rem; }
          .abstract-desc { font-size: 1.25rem; }
          .vision-wrapper { gap: 60px; }
        }
      `}</style>
    </section>
  );
};

export default VisionValues;